/* Progressive enhancement for the native Object Editions stage and room edit.
 * Every product, image and destination is server rendered. No commerce data,
 * product fetches, storage, or cart logic are owned by this component.
 */
(() => {
  "use strict";
  if (window.CiteraEditionStage) {
    window.CiteraEditionStage.init(document);
    return;
  }
  const instances = new WeakMap();
  const roomInstances = new WeakMap();
  function enhanceRooms(rooms) {
    if (roomInstances.has(rooms)) return roomInstances.get(rooms);
    const panels = Array.from(rooms.querySelectorAll("[data-edition-room-panel]"));
    const buttons = Array.from(rooms.querySelectorAll("[data-edition-room-target]"));
    const controls = rooms.querySelector("[data-edition-room-controls]");
    const fallback = rooms.querySelector("[data-edition-room-fallback]");
    if (!panels.length || !controls || !buttons.length) return null;
    function select(id, announce = false) {
      const selected = panels.find((panel) => panel.dataset.editionRoomPanel === id) || panels[0];
      const restoreFocus = panels.some(
        (panel) => panel !== selected && panel.contains(document.activeElement),
      );
      panels.forEach((panel) => {
        panel.hidden = panel !== selected;
      });
      buttons.forEach((button) => {
        button.setAttribute(
          "aria-pressed",
          String(button.dataset.editionRoomTarget === selected.dataset.editionRoomPanel),
        );
      });
      const activeButton = buttons.find(
        (button) => button.dataset.editionRoomTarget === selected.dataset.editionRoomPanel,
      );
      if (restoreFocus && activeButton) activeButton.focus();
      if (announce) {
        const status = rooms.querySelector("[data-edition-room-status]");
        if (status) status.textContent = selected.dataset.roomLabel;
      }
    }
    rooms.addEventListener("click", (event) => {
      const button = event.target.closest("[data-edition-room-target]");
      if (!button || !rooms.contains(button)) return;
      select(button.dataset.editionRoomTarget, true);
    });
    controls.hidden = false;
    if (fallback) fallback.hidden = true;
    rooms.classList.add("is-enhanced");
    const initial = panels.find((panel) => panel.id === window.location.hash.slice(1)) || panels[0];
    select(initial.dataset.editionRoomPanel);
    const instance = { select };
    roomInstances.set(rooms, instance);
    return instance;
  }
  function enhance(stage) {
    if (instances.has(stage)) return instances.get(stage);
    const panels = Array.from(stage.querySelectorAll("[data-edition-panel]"));
    const selectors = Array.from(stage.querySelectorAll("[data-edition-target]"));
    if (!panels.length) return null;
    let activePanel = panels[0];
    function lens(panel, name) {
      const views = Array.from(panel.querySelectorAll("[data-edition-view]"));
      const active = views.find((view) => view.dataset.editionView === name) || views[0];
      if (!active) return;
      views.forEach((view) => {
        view.hidden = view !== active;
      });
      panel.querySelectorAll("[data-edition-lens]").forEach((button) => {
        button.setAttribute(
          "aria-pressed",
          String(button.dataset.editionLens === active.dataset.editionView),
        );
      });
    }
    function select(id, announce = false) {
      const selected = panels.find((panel) => panel.dataset.editionPanel === id) || panels[0];
      panels.forEach((panel) => {
        panel.hidden = panel !== selected;
      });
      selectors.forEach((link) => {
        if (link.dataset.editionTarget === selected.dataset.editionPanel)
          link.setAttribute("aria-current", "true");
        else link.removeAttribute("aria-current");
      });
      activePanel = selected;
      lens(selected, "object");
      if (announce) {
        const status = stage.querySelector("[data-edition-status]");
        if (status) status.textContent = selected.dataset.productTitle;
      }
    }
    panels.forEach((panel) => {
      panel.querySelectorAll("[data-edition-lens-controls]").forEach((control) => {
        control.hidden = false;
      });
      lens(panel, "object");
    });
    stage.addEventListener("click", (event) => {
      const target = event.target.closest("[data-edition-target]");
      if (target && stage.contains(target)) {
        event.preventDefault();
        select(target.dataset.editionTarget, true);
      }
      const button = event.target.closest("[data-edition-lens]");
      if (button && activePanel.contains(button)) lens(activePanel, button.dataset.editionLens);
    });
    stage.classList.add("is-enhanced");
    const initialHash = window.location.hash.slice(1);
    const hashPanel = panels.find((panel) => panel.id === initialHash);
    select(hashPanel ? hashPanel.dataset.editionPanel : panels[0].dataset.editionPanel);
    const instance = { select };
    instances.set(stage, instance);
    return instance;
  }
  function init(scope) {
    if (scope.matches && scope.matches("[data-edition-stage]")) enhance(scope);
    scope.querySelectorAll("[data-edition-stage]").forEach(enhance);
    if (scope.matches && scope.matches("[data-edition-rooms]")) enhanceRooms(scope);
    scope.querySelectorAll("[data-edition-rooms]").forEach(enhanceRooms);
  }
  window.CiteraEditionStage = { init };
  init(document);
  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:block:select", (event) => {
    const roomPanel = event.target.closest("[data-edition-room-panel]");
    if (roomPanel) {
      const rooms = roomPanel.closest("[data-edition-rooms]");
      const instance = rooms && enhanceRooms(rooms);
      if (instance) instance.select(roomPanel.dataset.editionRoomPanel);
      return;
    }
    const panel = event.target.closest("[data-edition-panel]");
    if (!panel) return;
    const stage = panel.closest("[data-edition-stage]");
    if (!stage) return;
    const instance = enhance(stage);
    if (instance) instance.select(panel.dataset.editionPanel);
  });
})();
